import { useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, Divider, DrawerBody, DrawerCloseButton, DrawerFooter, DrawerHeader, HStack, Text, Tooltip } from "@chakra-ui/react"
import { CustomTable } from "../../../../components"
import { Column } from "../../../../components/CustomTable/customTable"
import { CustomToast } from "../../../../components";
import MoveMembersModal from "../MoveMemberModal/moveMemberModal";

const ManagementTeamDrawer = ({ selectedLeader, member, reload, setReload }: any) => {
    const { addToast } = CustomToast();
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [moveOpen, setMoveOpen] = useState(false);

    const [localMembers, setLocalMembers] = useState<any[]>(member || []);

    useEffect(() => {
        setLocalMembers(member || []);
        setCheckedIds([]);
    }, [member, selectedLeader]);

    const lockedBranchId = useMemo(() => {
        if (checkedIds.length === 0) return null;
        const firstChecked = localMembers.find((m: any) => (m.employeeId ?? m.id) === checkedIds[0]);
        return firstChecked?.branch_Id ?? null;
    }, [checkedIds, localMembers]);

    const isSelectable = (row: any) => lockedBranchId === null || row.branch_Id === lockedBranchId;

    const toggle = (row: any) => {
        const id = row.employeeId ?? row.id;
        setCheckedIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(x => x !== id);
            }
            if (!isSelectable(row)) {
                addToast({ message: 'Only members from the same branch can be selected together', status: 'warning' });
                return prev;
            }
            return [...prev, id];
        });
    };

    const selectableRows = useMemo(() => {
        const branchId = lockedBranchId ?? localMembers[0]?.branch_Id ?? null;
        return localMembers.filter((m: any) => m.branch_Id === branchId);
    }, [localMembers, lockedBranchId]);

    const allSelectableChecked = selectableRows.length > 0 && selectableRows.every((m: any) => checkedIds.includes(m.employeeId ?? m.id));
    const someSelectableChecked = selectableRows.some((m: any) => checkedIds.includes(m.employeeId ?? m.id));

    const toggleAll = (checked: boolean) => {
        setCheckedIds(checked ? selectableRows.map((m: any) => m.employeeId ?? m.id) : []);
    };

    const selectedBranchIds = useMemo(() => {
        return lockedBranchId !== null ? [lockedBranchId] : [];
    }, [lockedBranchId]);

    const handleMoved = () => {
        setLocalMembers(prev => prev.filter((m: any) => !checkedIds.includes(m.employeeId ?? m.id)));
        setCheckedIds([]);
        setMoveOpen(false);
        reload?.();
        setReload?.(true);
    };

    return (
        <Box overflowY="auto" h="100%">

            <DrawerCloseButton />
            <DrawerHeader>
                <Text className="font-poppins text_2xl text_medium">
                    Team of <Text as="span" className='font-poppins text_2xl text_medium'>{(selectedLeader?.name)}</Text>
                </Text>
            </DrawerHeader>
            <Divider mb={2} />
            <DrawerBody>
                <Box className="table_wrapper" mt={2}>
                    <CustomTable value={localMembers}>
                        <Column header={
                            <Checkbox colorScheme="green" isChecked={allSelectableChecked} isIndeterminate={someSelectableChecked && !allSelectableChecked} onChange={(e) => toggleAll(e.target.checked)} />}
                            body={(row: any) => {
                                const id = row.employeeId ?? row.id;
                                const checked = checkedIds.includes(id);
                                const disabled = !checked && !isSelectable(row);
                                const checkbox = (
                                    <Checkbox colorScheme="green" isChecked={checked} isDisabled={disabled} onChange={() => toggle(row)} />
                                );
                                return disabled ? (
                                    <Tooltip label="Different branch — clear current selection first" fontSize="11px" hasArrow>
                                        <Box display="inline-block">{checkbox}</Box>
                                    </Tooltip>
                                ) : checkbox;
                            }}
                        />
                        <Column header="S.No" body={(_: any, index: number) => <Text>{index + 1}</Text>} />
                        <Column field="emp_Name" header="Name" />
                        <Column field="phone" header="Phone" />
                        <Column field="email" header="Email" />
                        <Column field="company" header="Company" />
                        <Column field="branch" header="Branch" />
                        <Column field="department" header="Department" />
                    </CustomTable>
                </Box>
            </DrawerBody>
            <DrawerFooter>
                <HStack w="100%" justify="space-between">
                    <Text fontSize="12px" color="#94A3B8">{checkedIds.length} member(s) selected</Text>
                    <Button bg="#006838" color="white" _hover={{ bg: "#005530" }} isDisabled={checkedIds.length === 0} onClick={() => setMoveOpen(true)}>
                        Move Selected
                    </Button>
                </HStack>
            </DrawerFooter>
            <MoveMembersModal isOpen={moveOpen} onClose={() => setMoveOpen(false)} memberIds={checkedIds} currentLeaderId={selectedLeader?.employeeId ?? selectedLeader?.id} roleType={selectedLeader?.role} branchIds={selectedBranchIds} onMoved={handleMoved} />
        </Box>
    )
}

export default ManagementTeamDrawer;